import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { EXCHANGE_RATE_VALUE_MAX_DIGITS } from 'lumen/models/currency-conversion/exchange-rate';
import { timeout } from 'lumen/utils/waiters';
import { localCopy } from 'tracked-toolbox';

import styles from './td-number.module.css';

interface SettingsCurrencyConversionTablesCellsTdNumberSignature {
  Args: {
    cellData: number | string;
    column: {
      isReadOnly?: boolean;
      locale?: string;
      placeholder: string;
      tdNumberDataTestId: string;
      valueKey: string;
    };
    row: {
      [key: string]: unknown;
      hasUnsavedChanges?: boolean;
      onBlur?: () => void;
      updateInputValue: (newValue: number | null) => void;
    };
    rowMeta: {
      isSelected: boolean;
    };
  };
}

export default class SettingsCurrencyConversionTablesCellsTdNumber extends Component<SettingsCurrencyConversionTablesCellsTdNumberSignature> {
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isFocused: boolean = false;
  @localCopy('args.cellData') declare tempValue: number | string | null;

  trackExchangeRateChangedTask = task(
    { restartable: true },
    async (): Promise<void> => {
      await timeout();

      this.usageAnalytics.track('Exchange Rate Changed', {
        eventTrigger: 'exchange-rate-changed',
      });
    },
  );

  get hasErrors(): boolean {
    if (
      this.numericTempValue === null ||
      isNaN(this.numericTempValue) ||
      this.numericTempValue <= 0
    ) {
      return true;
    }
    return this.numericTempValue >= 10 ** EXCHANGE_RATE_VALUE_MAX_DIGITS;
  }

  get numericTempValue(): number | null {
    return typeof this.tempValue === 'number' ? this.tempValue : null;
  }

  get showUnsavedChangesBullet(): boolean {
    if (this.isFocused || this.args.column.isReadOnly || this.hasErrors) {
      return false;
    }
    return Boolean(this.args.row.hasUnsavedChanges);
  }

  @action
  onFocusIn(): void {
    this.isFocused = true;
  }

  @action
  onFocusOut(): void {
    this.isFocused = false;
    this.args.row.onBlur?.();
  }

  @action
  onValueChange(value: number | null): void {
    this.tempValue = value;
    this.args.row.updateInputValue(value);
    void this.trackExchangeRateChangedTask.perform();
  }

  <template>
    {{#if @column.isReadOnly}}
      {{@cellData}}
    {{else}}
      <div class="relative flex">
        <PrismNumberInput
          @hasErrors={{this.hasErrors}}
          @isSmall={{true}}
          @locale={{@column.locale}}
          @min={{0}}
          @onFocusIn={{this.onFocusIn}}
          @onFocusOut={{this.onFocusOut}}
          @onValueChange={{this.onValueChange}}
          @placeholder="0.000000"
          @removePadding={{true}}
          @step={{0.000001}}
          @value={{this.numericTempValue}}
          data-test-currency-conversion-rate-input
        />
        {{#if this.showUnsavedChangesBullet}}
          <div class={{styles.edited-value-bullet}}></div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
